import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { SmartMatchState, UseSmartMatchReturn } from "../../hooks/useSmartMatch";
import {
  TableData,
  performFuzzySearch,
  FuzzySearchRequest,
  generateGroundingTable,
  GroundingTableRequest
} from "../../../shared/apiService";
import { truncateTableDataForFuzzyMatch, MAX_FUZZY_MATCH_ROWS } from "../../../shared/excelDataExtractor";
import { generateTimestamp } from "../Market-Watch";
import SpreetailHeader from "../layout/SpreetailHeader";
import useSearchApiProgress from "./SearchApiProgress";
import ProgressSheet from "../common/ProgressSheet";

interface MapColumnsProps {
  onNext?: () => void;
  onBack?: () => void;
  smartMatch: UseSmartMatchReturn;
}

const MapColumns: React.FC<MapColumnsProps> = ({ onNext = () => { }, onBack = () => { }, smartMatch }) => {
  const [pickColumnsLeftOptions, setPickColumnsLeftOptions] = useState<string[]>([]);
  const [pickColumnsRightOptions, setPickColumnsRightOptions] = useState<string[]>([]);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(true);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFuzzyValidated, setIsFuzzyValidated] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [truncationWarning, setTruncationWarning] = useState<string | null>(null);
  const { progress, result, startSearch, isLoading, stopListening } = useSearchApiProgress();

  const [jobId, setJobId] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    setError(smartMatch.state.error);
  }, [smartMatch.state.error]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);


  useEffect(() => {
    if (smartMatch.state.leftTableData) {
      setPickColumnsLeftOptions(smartMatch.state.leftTableData.headers);
    }

    if (smartMatch.state.rightTableData) {
      setPickColumnsRightOptions(smartMatch.state.rightTableData.headers);
    }
  }, [smartMatch.state.leftTableData, smartMatch.state.rightTableData]);

  useEffect(() => {
    if (smartMatch.state.columnMappings.length > 0 &&
      smartMatch.state.leftTableData &&
      smartMatch.state.rightTableData &&
      !smartMatch.state.groundedTable &&
      !smartMatch.state.isGroundingLoading) {
      console.log('🎯 Auto-generating grounding table info and arranging layout...');
      generateGroundingTableInfo();
    }
  }, [smartMatch.state.columnMappings.length, smartMatch.state.leftTableData, smartMatch.state.rightTableData]);

  const getMappedColumns = () => {
    const leftCols = new Set<string>();
    const rightCols = new Set<string>();

    smartMatch.state.columnMappings.forEach(mapping => {
      leftCols.add(mapping.left);
      rightCols.add(mapping.right);
    });

    return {
      leftColumns: Array.from(leftCols),
      rightColumns: Array.from(rightCols)
    };
  };

  const { leftColumns: mappedLeftColumns, rightColumns: mappedRightColumns } = getMappedColumns();

  const getAllAvailableColumns = () => {
    return {
      leftColumns: smartMatch.state.leftTableData?.headers || [],
      rightColumns: smartMatch.state.rightTableData?.headers || []
    };
  };

  const { leftColumns, rightColumns } = getAllAvailableColumns();

  const hasInitializedOutputColumns = React.useRef(false);
  useEffect(() => {
    if (smartMatch.state.columnMappings.length > 0 &&
      leftColumns.length > 0 &&
      rightColumns.length > 0 &&
      !hasInitializedOutputColumns.current) {
      const allColumnIds = [
        ...leftColumns.map(col => `left_${col}`),
        ...rightColumns.map(col => `right_${col}`)
      ];
      smartMatch.setAllOutputColumns(allColumnIds);
      hasInitializedOutputColumns.current = true;
      console.log('✅ Initialized all output columns:', allColumnIds.length);
    }
  }, [smartMatch.state.columnMappings.length, leftColumns.length, rightColumns.length]);


 
  const handleColumnToggle = (side: 'left' | 'right', columnName: string) => {
    const columnId = `${side}_${columnName}`;

    const isMapped = side === 'left'
      ? mappedLeftColumns.includes(columnName)
      : mappedRightColumns.includes(columnName);

    if (isMapped) {
      return;
    }

    smartMatch.toggleOutputColumn(columnId);
  };

  const areAllLeftColumnsSelected = () => {
    if (leftColumns.length === 0) return false;
    const leftColumnIds = leftColumns.map(col => `left_${col}`);
    return leftColumnIds.every(id => smartMatch.state.selectedOutputColumns.has(id));
  };

  const areAllRightColumnsSelected = () => {
    if (rightColumns.length === 0) return false;
    const rightColumnIds = rightColumns.map(col => `right_${col}`);
    return rightColumnIds.every(id => smartMatch.state.selectedOutputColumns.has(id));
  };

  const handleSelectAllLeft = () => {
    const leftColumnIds = leftColumns.map(col => `left_${col}`);
    const rightColumnIds = rightColumns.map(col => `right_${col}`);
    const allSelected = areAllLeftColumnsSelected();
    const selectedRightColumns = rightColumnIds.filter(id =>
      smartMatch.state.selectedOutputColumns.has(id)
    );

    if (allSelected) {
      const selectedLeftColumns = leftColumnIds.filter(id => {
        const colName = id.replace('left_', '');
        return mappedLeftColumns.includes(colName);
      });
      smartMatch.setAllOutputColumns([...selectedLeftColumns, ...selectedRightColumns]);
    } else {
      const allSelectedColumns = [...leftColumnIds, ...selectedRightColumns];
      smartMatch.setAllOutputColumns(allSelectedColumns);
    }
  };

  const handleSelectAllRight = () => {
    const leftColumnIds = leftColumns.map(col => `left_${col}`);
    const rightColumnIds = rightColumns.map(col => `right_${col}`);
    const allSelected = areAllRightColumnsSelected();
    const selectedLeftColumns = leftColumnIds.filter(id =>
      smartMatch.state.selectedOutputColumns.has(id)
    );

    if (allSelected) {
      const selectedRightColumns = rightColumnIds.filter(id => {
        const colName = id.replace('right_', '');
        return mappedRightColumns.includes(colName);
      });
      smartMatch.setAllOutputColumns([...selectedLeftColumns, ...selectedRightColumns]);
    } else {
      const allSelectedColumns = [...selectedLeftColumns, ...rightColumnIds];
      smartMatch.setAllOutputColumns(allSelectedColumns);
    }
  };

  const swapColumnMappings = () => {
    console.log('🔄 Swapping column mappings to match new table positions...');
    console.log('📊 Before swap - Sample mapping:', smartMatch.state.columnMappings[0]);
    console.log('📊 Before swap - Output columns:', Array.from(smartMatch.state.selectedOutputColumns));

    smartMatch.swapColumnMappingsInState();

    console.log('✅ Column mappings, mapping groups, and output columns swapped successfully');
  };

  const arrangeGroundedTableToLeft = async (groundedTableName: string) => {
    if (!smartMatch.state.leftTableData || !smartMatch.state.rightTableData) {
      return;
    }

    const currentLeftTableName = smartMatch.state.leftTableData.sheetName;
    const currentRightTableName = smartMatch.state.rightTableData.sheetName;

    if (groundedTableName === currentRightTableName && groundedTableName !== currentLeftTableName) {
      console.log('🔄 Auto-arranging: Moving grounded table to left side');

      const tempSource = smartMatch.state.leftDataSource;
      const tempData = smartMatch.state.leftTableData;

      smartMatch.setLeftDataSource(smartMatch.state.rightDataSource || '', smartMatch.state.rightTableData);
      smartMatch.setRightDataSource(tempSource || '', tempData);

      swapColumnMappings();

      console.log(`✅ Grounded table "${groundedTableName}" is now on the left side`);
    } else if (groundedTableName === currentLeftTableName) {
      console.log(`✅ Grounded table "${groundedTableName}" is already on the left side`);
    } else {
      console.log(`⚠️ Warning: Grounded table "${groundedTableName}" not found in current tables`);
    }
  };

  const generateGroundingTableInfo = async () => {
    if (!smartMatch.state.leftTableData || !smartMatch.state.rightTableData || smartMatch.state.columnMappings.length === 0) {
      console.log('⚠️ Cannot generate grounding table: missing data or mappings');
      return;
    }

    try {
      smartMatch.setGroundingLoading(true);
      smartMatch.setError(null);

      const leftTableData: TableData = {
        table_name: smartMatch.state.leftTableData.sheetName,
        headers: smartMatch.state.leftTableData.headers,
        rows: smartMatch.state.leftTableData.data
      };

      const rightTableData: TableData = {
        table_name: smartMatch.state.rightTableData.sheetName,
        headers: smartMatch.state.rightTableData.headers,
        rows: smartMatch.state.rightTableData.data
      };

      const matchedColumns = smartMatch.state.columnMappings.map(mapping => ({
        left_column: mapping.left,
        right_column: mapping.right
      }));

      const request: GroundingTableRequest = {
        left_table: leftTableData,
        right_table: rightTableData,
        matched_columns: matchedColumns
      };

      console.log('🎯 Generating grounding table with LLM...');
      const response = await generateGroundingTable(request);

      smartMatch.setGroundingInfo(response.grounded_table, response.reasoning);

      console.log('✅ Grounding table determined:', response.grounded_table);
      console.log('📝 Reasoning:', response.reasoning);

      await arrangeGroundedTableToLeft(response.grounded_table);

    } catch (error) {
      console.error('❌ Error generating grounding table:', error);
      smartMatch.setError(`Failed to determine grounding table: ${error.message}`);
    } finally {
      smartMatch.setGroundingLoading(false);
    }
  };

  const handleIntelligentSwap = async () => {
    const leftTableNameBefore = smartMatch.state.leftTableData?.sheetName;
    const rightTableNameBefore = smartMatch.state.rightTableData?.sheetName;
    const groundedTableBefore = smartMatch.state.groundedTable;

    console.log('📊 Before swap:', {
      left: leftTableNameBefore,
      right: rightTableNameBefore,
      grounded: groundedTableBefore
    });

    // ✅ NEW: Clear formulas BEFORE swapping
    // console.log('🔄 Clearing cached formulas due to table swap');
    // smartMatch.clearCachedMatchingFormulas();

    // Perform the physical swap
    const tempSource = smartMatch.state.leftDataSource;
    const tempData = smartMatch.state.leftTableData;

    if (smartMatch.state.rightDataSource && smartMatch.state.rightTableData) {
      smartMatch.setLeftDataSource(smartMatch.state.rightDataSource, smartMatch.state.rightTableData);
      smartMatch.setRightDataSource(tempSource || '', tempData || smartMatch.state.rightTableData);

      console.log('🔄 Tables swapped manually by user');

      swapColumnMappings();

      if (groundedTableBefore && smartMatch.state.groundingReasoning) {
        const newGroundedTable = rightTableNameBefore;

        smartMatch.setGroundingInfo(
          newGroundedTable,
          `User manually swapped tables. Previous grounding: ${smartMatch.state.groundingReasoning}`
        );

        console.log('📊 After swap:', {
          left: rightTableNameBefore,
          right: leftTableNameBefore,
          grounded: newGroundedTable,
          position: 'left'
        });
      } else {
        setTimeout(async () => {
          await generateGroundingTableInfo();
        }, 100);
      }
    }
  };

  const handleThresholdChange = (value: number) => {
    smartMatch.setThreshold(value);
  };

  async function upsertFuzzyWorksheet(context: Excel.RequestContext) {
    const timestamp = generateTimestamp();
    const baseName = `SM Data ${timestamp}`;
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();
  
    const existingNames = worksheets.items.map((ws) => ws.name);
  
    // Generate unique sheet name with incremental suffix if needed
    let uniqueSheetName = baseName;
    if (existingNames.includes(baseName)) {
      let counter = 2;
      uniqueSheetName = `${baseName}_${String(counter).padStart(2, "0")}`;
      
      const MAX_ATTEMPTS = 100;
      while (existingNames.includes(uniqueSheetName) && counter < MAX_ATTEMPTS) {
        counter++;
        uniqueSheetName = `${baseName}_${String(counter).padStart(2, "0")}`;
      }
  
      if (existingNames.includes(uniqueSheetName)) {
        throw new Error(`Unable to generate a unique sheet name after ${MAX_ATTEMPTS} attempts.`);
      }
    }
  
    const fuzzySheet = worksheets.add(uniqueSheetName);
    fuzzySheet.activate();
    return fuzzySheet;
  }

  const populateExcelWithData = async (
    data: any[],
    returnedColumns?: string[],
    verificationFailed?: boolean,
    failureMessage?: string
  ) => {
    try {
      await Excel.run(async (context) => {
        const worksheet = await upsertFuzzyWorksheet(context);

        const usedRange = worksheet.getUsedRangeOrNullObject();
        usedRange.load("address");
        await context.sync();

        if (!usedRange.isNullObject) {
          usedRange.clear(Excel.ClearApplyTo.contents);
        }

        if (data.length === 0 && !verificationFailed) {
          console.log('No data to populate and no verification failure');
          return;
        }

        if (verificationFailed && data.length === 0) {
          console.log('⚠️ Verification failed with no results - showing message only');

          const messageRange = worksheet.getRange('A1');
          messageRange.values = [[`⚠️ Verification Failed: ${failureMessage}`]];

          messageRange.format.font.bold = true;
          messageRange.format.font.size = 12;
          messageRange.format.font.color = '#DC2626';
          messageRange.format.fill.color = '#FEE2E2';
          messageRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
          messageRange.format.verticalAlignment = Excel.VerticalAlignment.center;
          messageRange.format.rowHeight = 50;
          messageRange.format.wrapText = true;

          const noteRange = worksheet.getRange('A3');
          noteRange.values = [['No matching results were found. Please review your column mappings and try again.']];
          noteRange.format.font.italic = true;
          noteRange.format.font.color = '#6B7280';
          noteRange.format.wrapText = true;

          worksheet.getUsedRange().format.autofitColumns();
          await context.sync();
          return;
        }

        if (!data || data.length === 0 || !data[0]) {
          console.error('❌ Invalid data structure:', data);
          throw new Error('Invalid data structure received from API');
        }

        let orderedColumns: string[];

        if (returnedColumns && returnedColumns.length > 0) {
          console.log('📋 Using API-filtered columns:', returnedColumns);

          const leftCols = returnedColumns.filter(col => col.startsWith('left_'));
          const rightCols = returnedColumns.filter(col => col.startsWith('right_'));
          const otherCols = returnedColumns.filter(col =>
            !col.startsWith('left_') && !col.startsWith('right_')
          );

          orderedColumns = [...leftCols, ...rightCols, ...otherCols];

          console.log('📋 Reordered columns - left first, right second:', orderedColumns);
        } else {
          console.log('📋 No filtered columns provided, using all columns from data');
          const allColumns = Object.keys(data[0] || {});
          const columnsToExclude = ['matched_on_left', 'matched_on_right', 'left_value', 'right_value'];
          const dataColumns = allColumns.filter(col => !columnsToExclude.includes(col));

          const leftColumns = dataColumns.filter(col => col.startsWith('left_'));
          const rightColumns = dataColumns.filter(col => col.startsWith('right_'));
          const otherColumns = dataColumns.filter(col => !col.startsWith('left_') && !col.startsWith('right_'));
          orderedColumns = [...leftColumns, ...rightColumns, ...otherColumns];
        }

        if (orderedColumns.length === 0) {
          console.error('❌ No columns found in data');
          throw new Error('No columns found in data structure');
        }

        console.log('📋 Ordered columns for Excel:', orderedColumns);

        const baseNames = orderedColumns.map(col => {
          if (col.startsWith('left_')) return col.replace('left_', '');
          if (col.startsWith('right_')) return col.replace('right_', '');
          return col;
        });

        const nameCounts = new Map<string, number>();
        baseNames.forEach(name => {
          nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
        });

        const displayHeaders = orderedColumns.map(col => {
          let baseName: string;

          if (col.startsWith('left_')) {
            baseName = col.replace('left_', '');
            if ((nameCounts.get(baseName) || 0) > 1) {
              return `Left_${baseName}`;
            }
            return baseName;
          } else if (col.startsWith('right_')) {
            baseName = col.replace('right_', '');
            if ((nameCounts.get(baseName) || 0) > 1) {
              return `Right_${baseName}`;
            }
            return baseName;
          } else if (col === 'validation_message') {
            return 'Validation Message';
          }
          else {
            return col;
          }
        });
        console.log('📋 Display headers:', displayHeaders);

        const getColumnLetter = (index: number): string => {
          let letter = '';
          while (index >= 0) {
            letter = String.fromCharCode(65 + (index % 26)) + letter;
            index = Math.floor(index / 26) - 1;
          }
          return letter;
        };

        const lastColumn = getColumnLetter(displayHeaders.length - 1);
        console.log(`📋 Excel range will be from column A to ${lastColumn}`);

        let currentRow = 1;

        if (verificationFailed && failureMessage) {
          console.log('⚠️ Inserting verification failure message in Excel');

          const messageRange = worksheet.getRange(`A1:${lastColumn}1`);
          messageRange.merge(true);
          messageRange.getCell(0, 0).values = [[`⚠️ Validation Results: ${failureMessage}`]];

          messageRange.format.font.bold = true;
          messageRange.format.font.size = 12;
          messageRange.format.font.color = '#DC2626';
          messageRange.format.fill.color = '#FEE2E2';
          messageRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
          messageRange.format.verticalAlignment = Excel.VerticalAlignment.center;
          messageRange.format.wrapText = true;

          const firstRow = worksheet.getRange(`A1:${lastColumn}1`);
          firstRow.format.fill.color = '#FEE2E2';
          firstRow.format.rowHeight = 50;

          currentRow = 2;

          console.log('✅ Failure message inserted at row 1');
        }

        const headerRange = worksheet.getRange(`A${currentRow}:${lastColumn}${currentRow}`);
        headerRange.values = [displayHeaders];

        currentRow++;

        if (data.length > 0) {
          console.log(`📊 Processing ${data.length} data rows with ${orderedColumns.length} columns`);

          const dataValues = data.map((row, rowIndex) => {
            if (!row || typeof row !== 'object') {
              console.error(`❌ Invalid row ${rowIndex}:`, row);
              return orderedColumns.map(() => '');
            }

            return orderedColumns.map(col => {
              const value = row[col];
              if (value === null || value === undefined || value === '') {
                return '';
              }
              return String(value);
            });
          });

          const expectedRows = data.length;
          const expectedCols = orderedColumns.length;
          const actualRows = dataValues.length;
          const actualCols = dataValues[0]?.length || 0;

          console.log(`📐 Expected: ${expectedRows} rows × ${expectedCols} cols`);
          console.log(`📐 Actual: ${actualRows} rows × ${actualCols} cols`);

          if (actualRows !== expectedRows || actualCols !== expectedCols) {
            console.error('❌ Dimension mismatch detected!');
            throw new Error(`Data dimension mismatch: expected ${expectedRows}×${expectedCols}, got ${actualRows}×${actualCols}`);
          }

          const dataRange = worksheet.getRange(`A${currentRow}:${lastColumn}${currentRow + data.length - 1}`);
          dataRange.numberFormat = data.map(() => orderedColumns.map(() => "@"));
          dataRange.values = dataValues;

          console.log(`✅ Inserted ${data.length} data rows starting from row ${currentRow}`);

          // Create Excel Table over header + data range
          const tableStartRow = verificationFailed && failureMessage ? 2 : 1;
          const tableRange = worksheet.getRange(
            `A${tableStartRow}:${lastColumn}${tableStartRow + data.length}`
          );
          const table = worksheet.tables.add(tableRange, true);
          table.name = `SMData_${Date.now()}`;
          table.style = "TableStyleMedium2";
        }

        worksheet.getUsedRange().format.autofitColumns();

        console.log(`✅ Successfully populated ${data.length} rows × ${displayHeaders.length} columns`);

        await context.sync();
      });
    } catch (error) {
      console.error('❌ Error populating Excel:', error);
      throw error;
    }
  };

  const isStateChanged = (prevState: SmartMatchState, newState: SmartMatchState) => {
    let is_state_changed = false;

    if(newState.matchingIntent !== prevState.matchingIntent) {
      is_state_changed = true;
    }

    if(newState.leftDataSource !== prevState.leftDataSource || newState.rightDataSource !== prevState.rightDataSource) {
      is_state_changed = true;
    }

    const newMappings = newState.columnMappings.map(mapping => `${mapping.left}_${mapping.right}`);
    const prevMappings = prevState.columnMappings.map(mapping => `${mapping.left}_${mapping.right}`);

    if(newMappings.length !== prevMappings.length || newMappings.some(row => !prevMappings.includes(row))) {
      is_state_changed = true;
    }

    if(newState.groundedTable !== prevState.groundedTable) {
      is_state_changed = true;
    }

    return is_state_changed;
  }

  const handleRunMatch = async () => {
    try {
      console.log('🚀 Starting Smart Match...');
      setIsGenerating(true);
      setError(null);
      setTruncationWarning(null);
      smartMatch.setError(null);
      stopListening();

      if (!smartMatch.state.leftTableData || !smartMatch.state.rightTableData) {
        throw new Error("Table data is missing. Please go back and select data sources.");
      }

      if (smartMatch.state.columnMappings.length === 0) {
        throw new Error("No column mappings defined. Please map at least one column.");
      }

      const leftTableData = truncateTableDataForFuzzyMatch(smartMatch.state.leftTableData);
      const rightTableData = truncateTableDataForFuzzyMatch(smartMatch.state.rightTableData);

      const mappingGroups = smartMatch.state.mappingGroups || [];
      const hasMultipleGroups = mappingGroups.length > 1;
      const hasOrOperator = mappingGroups.some(group => group.operator === 'OR');
      const logicOperator = hasMultipleGroups || hasOrOperator ? 'OR' : 'AND';

      const match_groups = mappingGroups
        .filter(group => group.mappings.length > 0)
        .map(group => {
          const groupMappings: Array<{ left_column: string; right_column: string }> = [];

          group.mappings.forEach(mapping => {
            mapping.leftColumns.forEach(leftCol => {
              mapping.rightColumns.forEach(rightCol => {
                groupMappings.push({
                  left_column: leftCol,
                  right_column: rightCol
                });
              });
            });
          });

          return groupMappings;
        });

      const prevState = smartMatch.prevState;
      const newState = smartMatch.state;

      let is_state_changed = isStateChanged(prevState, newState);


      if(is_state_changed) {
        smartMatch.clearCachedMatchingFormulas();
      }


      const request: FuzzySearchRequest = {
        left_table: [
          leftTableData.headers,
          ...leftTableData.data
        ],
        right_table: [
          rightTableData.headers,
          ...rightTableData.data
        ],
        match_columns: logicOperator === 'AND' ? smartMatch.state.columnMappings.map(mapping => ({
          left_column: mapping.left,
          right_column: mapping.right
        })) : undefined,
        logic_operator: logicOperator,
        match_groups: logicOperator === 'OR' ? match_groups : undefined,
        threshold: smartMatch.state.threshold,
        max_match: smartMatch.state.maxMatches ?? undefined,
        output_columns: Array.from(smartMatch.state.selectedOutputColumns),
        user_intent: smartMatch.state.matchingIntent || undefined,

        ...(smartMatch.state.cachedMatchingFormulas && !is_state_changed && {
          matching_formulas: smartMatch.state.cachedMatchingFormulas
        })
      };

      console.log('📤 Final API Request:', {
        leftRows: request.left_table.length - 1,
        rightRows: request.right_table.length - 1,
        logic_operator: request.logic_operator,
        match_groups_count: request.match_groups?.length,
        match_columns_count: request.match_columns?.length,
        threshold: request.threshold,
        scorer: request.scorer,
        max_match: request.max_match,
        output_columns_count: request.output_columns?.length,
        output_columns: request.output_columns,
        user_intent: request.user_intent ? 'Provided' : 'Not provided',
        matching_formulas: request.matching_formulas ? '✅ Cached (skipping LLM)' : '❌ Not cached (will generate)'
      });

      await startSearch(request);
 
      console.log('✅ Smart Match completed successfully');
      onNext();
    } catch (error) {
      console.error('❌ Error in handleRunMatch:', error);
      const errorMessage = error.message || "An error occurred while performing the match";
      setError(errorMessage);
      smartMatch.setError(errorMessage);
      setIsFuzzyValidated(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const calculateOutputColumnHeight = () => {
    const maxColumns = Math.max(leftColumns.length, rightColumns.length);
    const minHeight = 80;
    const itemHeight = 40;
    const padding = 16;
    const maxHeight = 300;

    if (maxColumns === 0) {
      return minHeight;
    }

    const calculatedHeight = (maxColumns * itemHeight) + padding;
    return Math.min(calculatedHeight, maxHeight);
  };

  const outputColumnHeight = calculateOutputColumnHeight();

  const renderOutputColumnList = (
    title: string,
    columns: string[],
    side: 'left' | 'right'
  ) => {
    const areAllSelected = side === 'left' ? areAllLeftColumnsSelected() : areAllRightColumnsSelected();
    const handleSelectAll = side === 'left' ? handleSelectAllLeft : handleSelectAllRight;

    return (
      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-semibold text-gray-800 m-0 mb-2">
          {title}
        </h4>
        <div
          onClick={handleSelectAll}
          className="mb-2 py-1.5 text-xs text-gray-800 transition-all duration-200 flex items-center cursor-pointer hover:bg-gray-50 rounded font-semibold"
          style={{ paddingLeft: '17px' }}
          title={areAllSelected ? "Click to unselect all (mapped columns will remain selected)" : "Select all columns"}
        >
          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center mr-2 flex-shrink-0 ${areAllSelected
              ? 'bg-blue-500 border-gray-300'
              : 'bg-transparent border-gray-300'
            }`}>
            {areAllSelected && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <span>Select All</span>
        </div>
        <div
          className="scrollable-column-list bg-white border border-gray-200 rounded-md p-2 overflow-y-auto"
          style={{ height: `${outputColumnHeight}px` }}
        >
          {columns.length === 0 ? (
            <div className="text-center p-4 text-gray-400 text-xs">
              No columns available
            </div>
          ) : (
            columns.map((column) => {
              const columnId = `${side}_${column}`;
              const isSelected = smartMatch.state.selectedOutputColumns.has(columnId);
              const isMapped = side === 'left'
                ? mappedLeftColumns.includes(column)
                : mappedRightColumns.includes(column);

              return (
                <div
                  key={column}
                  onClick={() => handleColumnToggle(side, column)}
                  className={`p-2 px-3 text-xs transition-all duration-200 border-b border-gray-200 -mx-2 pr-5 break-words flex items-center ${isMapped
                      ? 'bg-blue-50 border-l-[4px] border-l-blue-700 pl-[16px] font-bold cursor-not-allowed opacity-90 text-gray-800'
                      : isSelected
                        ? 'bg-blue-100 border-l-[3px] border-l-blue-400 pl-[17px] font-semibold cursor-pointer text-gray-800'
                        : 'border-l-[3px] border-l-transparent pl-[17px] font-normal hover:bg-gray-50 cursor-pointer text-gray-800'
                    }`}
                  title={isMapped ? 'This is a mapped column (always included)' : ''}
                >
                  <div className={`w-4 h-4 border-2 rounded flex items-center justify-center mr-2 flex-shrink-0 ${isMapped
                      ? 'bg-blue-700 border-blue-700'
                      : isSelected
                        ? 'bg-blue-500 border-gray-300'
                        : 'bg-transparent border-gray-300'
                    }`}>
                    {(isSelected || isMapped) && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  {column}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };


  useEffect(() => {
    const handleResult = async () => {
      if (result) {
        console.log('result', result);
        const response = result as any;

        if (response.matching_formulas) {
          console.log('💾 Caching encrypted matching formulas for reuse');
          console.log('   Formula length:', response.matching_formulas.length, 'characters');
          smartMatch.setCachedMatchingFormulas(response.matching_formulas);
          smartMatch.updatePrevState(smartMatch.state);
        } else {
          console.log('⚠️ No matching formulas received from backend');
        }

        console.log('✅ Received fuzzy search response:', {
          totalMatches: response.total_matches,
          results: response.results.length,
          logic_operator: response.logic_operator,
          output_columns_applied: response.output_columns_applied,
          returned_columns: response.returned_columns,
          is_successful: response.is_successful,
          reasoning: response.reasoning,
          formulas_cached: !!response.matching_formulas
        });

        const verificationFailed = response.is_successful === false;
        setIsFuzzyValidated(response.is_successful);
        const failureMessage = response.reasoning || 'Verification failed. Please check your matching configuration.';

        let filteredResults = response.results;

        if (smartMatch.state.handleMissingValues === "Skip records") {
          const beforeCount = filteredResults.length;
          filteredResults = filteredResults.filter(row => {
            const score = row.match_score;
            return score !== null && score !== undefined && score > 0;
          });
          const removedCount = beforeCount - filteredResults.length;

          console.log(`🔍 Filtering applied: "Skip records" mode`);
          console.log(`   Total rows from API: ${beforeCount}`);
          console.log(`   Matched rows (score > 0): ${filteredResults.length}`);
          console.log(`   Unmatched rows removed: ${removedCount}`);
        }

        await populateExcelWithData(
          filteredResults,
          response.returned_columns,
          verificationFailed,
          failureMessage
        );
      }
    }
    handleResult();
  }, [result])

  const backButtonHandler = () => {
    stopListening();
    onBack();
  }

  return (
    <>
      <style>
        {`
          *::-webkit-scrollbar {
            display: none;
          }
          
          .scrollable-column-list::-webkit-scrollbar {
            display: none;
          }
          
          .scrollable-column-list {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
          
          .bg-white-99 {
            background-color: rgba(255, 255, 255, 0.6);
          }
          
          .bg-white-80 {
            background-color: rgba(255, 255, 255, 0.5);
          }
          
          .slider-gradient {
            background: linear-gradient(to right, #DC2626 0%, #F59E0B 50%, #10B981 100%);
          }
          
          .info-banner-bg {
            background-color: rgba(4, 97, 254, 0.1);
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
        <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
          style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
        <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
          style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

        <SpreetailHeader />

        <div
          className="flex flex-col items-center p-4 px-5 flex-1 overflow-y-auto"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          } as React.CSSProperties}
        >
          <div className="w-full max-w-[360px]">
            <div className="text-left mb-4">
              <h1 className="text-base font-semibold leading-none text-gray-800 m-0 mb-2.5">
                Smart match
              </h1>

              <div className="flex justify-start gap-1 mb-5">
                <div className="flex-1 h-1 bg-[#0461FE] rounded-sm"></div>
                <div className="flex-1 h-1 bg-[#0461FE] rounded-sm"></div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 bg-[#068E75] rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                  <span className="text-white text-xs font-semibold">2</span>
                </div>
                <h2 className="text-[15px] font-semibold text-gray-800 m-0">
                  Review and configure
                </h2>
              </div>

              <p className="text-[13px] text-gray-500 m-0 mb-4 ml-8">
                Review your settings and configure output options
              </p>

              <div className="bg-white-80 rounded-lg p-4 shadow-sm mb-6">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800 m-0 mb-1">
                    Output Columns
                  </h3>
                  <p className="text-xs text-gray-500 m-0">
                    Mapped columns are always included. Select additional columns as needed.
                  </p>
                </div>

                <div className="flex gap-3">
                  {renderOutputColumnList("Left Columns", leftColumns, 'left')}
                  {renderOutputColumnList("Right Columns", rightColumns, 'right')}
                </div>
              </div>

              <div
                className="bg-white-99 rounded-lg p-4 shadow-sm mb-6 cursor-pointer"
                onClick={(e) => {
                  if (e.currentTarget === e.target || (e.target as HTMLElement).closest('[data-adv-header]')) {
                    setIsAdvancedOpen(!isAdvancedOpen);
                  }
                }}
              >
                <div className="flex items-center mb-2" data-adv-header>
                  <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center mr-3 flex-shrink-0">
                    <img
                      src="assets/iconsax-keyboard-open.png"
                      alt="Advanced"
                      width="20"
                      height="20"
                      className="object-contain"
                    />
                  </div>
                  <div className="flex-1 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 m-0">
                        Advanced Configurations
                      </h3>
                      <p className="text-xs text-gray-500 m-0">
                        Threshold, missing values, max matches, and run match
                      </p>
                    </div>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className={`transition-transform duration-200 ${isAdvancedOpen ? 'rotate-180' : 'rotate-0'}`}
                    >
                      <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {isAdvancedOpen && (
                  <div className="mt-4">
                    {error && (
                      <div ref={errorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
                        <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-sm font-bold">!</span>
                        </div>
                        <p className="text-[13px] text-red-800 m-0 leading-snug">
                          {error}
                        </p>
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-800 m-0 mb-4">
                        Similarity threshold
                      </h3>

                      <div className="flex justify-between items-center mb-2">
                        <span className="text-base font-semibold text-blue-500">
                          {smartMatch.state.threshold}%
                        </span>

                        <span className="text-[11px] text-[#585858] font-medium">
                          Match Confidence
                        </span>
                      </div>

                      <div className="relative mb-2">
                        <div
                          className="h-1.5 slider-gradient rounded cursor-pointer relative"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const percentage = (x / rect.width) * 100;
                            const newValue = Math.round(percentage);
                            handleThresholdChange(Math.max(0, Math.min(100, newValue)));
                          }}
                        >
                          <div
                            className="absolute -top-1.5 w-[18px] h-[18px] bg-blue-500 rounded-full border-2 border-white shadow-md cursor-grab"
                            style={{
                              left: `${smartMatch.state.threshold}%`,
                              transform: 'translateX(-50%)'
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const startX = e.clientX;
                              const startValue = smartMatch.state.threshold;

                              const handleMouseMove = (moveEvent: MouseEvent) => {
                                const rect = (e.target as HTMLElement).parentElement?.getBoundingClientRect();
                                if (rect) {
                                  const deltaX = moveEvent.clientX - startX;
                                  const deltaPercentage = (deltaX / rect.width) * 100;
                                  const newValue = Math.max(0, Math.min(100, startValue + deltaPercentage));
                                  handleThresholdChange(Math.round(newValue));
                                }
                              };

                              const handleMouseUp = () => {
                                document.removeEventListener('mousemove', handleMouseMove);
                                document.removeEventListener('mouseup', handleMouseUp);
                              };

                              document.addEventListener('mousemove', handleMouseMove);
                              document.addEventListener('mouseup', handleMouseUp);
                            }}
                          ></div>
                        </div>

                        <div className="absolute top-3 left-0 text-[11px] text-gray-500">
                          Loose
                        </div>
                        <div className="absolute top-3 right-0 text-[11px] text-gray-500">
                          Strict
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-[13px] font-semibold text-gray-800 mb-2">
                        Handle Missing Values
                      </label>

                      <div className="relative">
                        <div
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="w-full p-2.5 px-3 bg-white border border-gray-300 rounded-md text-[13px] cursor-pointer flex justify-between items-center box-border"
                          style={{ color: smartMatch.state.handleMissingValues ? '#1f2937' : '#9CA3AF' }}
                        >
                          <span>{smartMatch.state.handleMissingValues || "Select option"}</span>
                          <svg
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                            fill="none"
                            className={`transition-transform duration-200 ${showDropdown ? 'rotate-180' : 'rotate-0'}`}
                          >
                            <path d="M1 1L6 6L11 1" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {showDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-md z-10 overflow-hidden">
                            <div
                              onClick={() => {
                                smartMatch.setHandleMissingValues("Skip records");
                                setShowDropdown(false);
                              }}
                              className="p-2.5 px-3 text-[13px] text-gray-800 cursor-pointer hover:bg-gray-50"
                              style={{
                                backgroundColor: smartMatch.state.handleMissingValues === "Skip records" ? "#F3F4F6" : "white"
                              }}
                            >
                              Skip records
                            </div>

                            <div
                              onClick={() => {
                                smartMatch.setHandleMissingValues("Flag for review");
                                setShowDropdown(false);
                              }}
                              className="p-2.5 px-3 text-[13px] text-gray-800 cursor-pointer hover:bg-gray-50"
                              style={{
                                backgroundColor: smartMatch.state.handleMissingValues === "Flag for review" ? "#F3F4F6" : "white"
                              }}
                            >
                              Flag for review
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-[13px] font-semibold text-gray-800 mb-2">
                        Max Matches
                      </label>

                      <div className="flex items-center gap-3 w-full">
                        <button
                          onClick={() => {
                            const current = smartMatch.state.maxMatches;
                            if (current === null || current <= 1) {
                              smartMatch.setMaxMatches(null);
                            } else {
                              smartMatch.setMaxMatches(current - 1);
                            }
                          }}
                          className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center cursor-pointer text-lg text-gray-500 transition-all duration-200 flex-shrink-0 hover:bg-gray-50 hover:text-gray-800"
                        >
                          −
                        </button>

                        <input
                          type="number"
                          value={smartMatch.state.maxMatches === null ? '' : smartMatch.state.maxMatches}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              smartMatch.setMaxMatches(null);
                            } else {
                              const numValue = parseInt(value);
                              if (!isNaN(numValue)) {
                                smartMatch.setMaxMatches(numValue);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              smartMatch.setMaxMatches(null);
                            } else {
                              const numValue = parseInt(value);
                              if (isNaN(numValue) || numValue < 1) {
                                smartMatch.setMaxMatches(null);
                              }
                            }
                          }}
                          className="flex-1 h-10 bg-white border border-gray-300 rounded-md outline-none text-center text-[13px] text-gray-800 transition-all duration-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]"
                        />

                        <button
                          onClick={() => {
                            const current = smartMatch.state.maxMatches;
                            if (current === null) {
                              smartMatch.setMaxMatches(1);
                            } else {
                              smartMatch.setMaxMatches(current + 1);
                            }
                          }}
                          className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center cursor-pointer text-lg text-gray-500 transition-all duration-200 flex-shrink-0 hover:bg-gray-50 hover:text-gray-800"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="w-full info-banner-bg rounded-lg p-2.5 flex items-center gap-2 mb-4 shadow-sm box-border">
                      <div className="w-5 h-5 bg-[#0461FE] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-semibold">
                          !
                        </span>
                      </div>

                      <span className="text-[13px] text-gray-800 font-normal">
                        Matched Results will open in a new sheet in your workbook
                      </span>
                    </div>
                  </div>
                )}
              </div>

                {/* Progress Sheet Overlay */}
                <ProgressSheet
                  isOpen={isLoading && progress !== undefined && progress !== null}
                  progress={progress ?? 0}
                  title="Matching data"
                  subtitle="Please wait while we find matching records"
                />

              <div className="flex gap-2.5">
                <button
                  onClick={backButtonHandler}
                  className="flex-1 bg-white text-[#068E75] border border-[#068E75] rounded-md p-2.5 px-4 text-[13px] font-semibold cursor-pointer transition-all duration-200 h-10 hover:bg-green-50 hover:-translate-y-px"
                >
                  Back
                </button>
                <button
                  onClick={handleRunMatch}
                  disabled={isLoading}
                  className={`flex-1 text-white border-none rounded-md p-2.5 px-4 text-[13px] font-semibold transition-all duration-200 shadow-md h-10 flex items-center justify-center gap-2 ${isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#068E75] cursor-pointer hover:bg-[#057A63] hover:-translate-y-px hover:shadow-lg'
                    }`}
                  style={{
                    boxShadow: isLoading ? 'none' : '0 2px 4px rgba(6, 142, 117, 0.3)'
                  }}
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: 'spin 1s linear infinite' }}></div>
                  )}
                  {isLoading ? "Matching..." : isFuzzyValidated ? "Run Match" : "To Proceed"}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MapColumns;